import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import {
  Blueprint,
  BlueprintGeneratorService,
} from 'src/app/presentations/services/blueprint-generator.service';
import { environment } from 'src/environments/environment';
import { Orientation } from 'src/app/api/services/presentation';
import { TemplateEditorUtilsService } from './template-editor-utils.service';

@Injectable({
  providedIn: 'root',
})
export class BlueprintService {
  private _blueprints = {};

  public loadingBlueprint = false;
  public blueprintData;

  constructor(
    private httpClient: HttpClient,
    private blueprintGeneratorService: BlueprintGeneratorService,
    private templateEditorUtils: TemplateEditorUtilsService,
  ) {}

  getBlueprintCached(productCode, readOnly?): Promise<any> {
    const blueprint = this._blueprints[productCode];

    if (blueprint) {
      if (!readOnly) {
        this.blueprintData = blueprint;
      }

      return Promise.resolve(blueprint);
    } else {
      return this._getBlueprint(productCode, readOnly);
    }
  }

  async generateBlueprintDataFromLayout(
    layout: string,
    branding?: boolean,
    orientation?: Orientation,
  ): Promise<Blueprint> {
    let width = 1920;
    let height = 1080;

    if (orientation === 'portrait') {
      width = 1080;
      height = 1920;
    }

    const blueprint = this.blueprintGeneratorService.generateBlueprint(layout, {
      width,
      height,
      branding,
    });
    this.blueprintData = blueprint;
    return blueprint;
  }

  _sanitizeBlueprint(blueprintData) {
    const components = (blueprintData && blueprintData.components) || [];

    components.forEach((component) => {
      // Retrieve playlist items from the blueprint
      if (component && component.type === 'rise-playlist') {
        const items =
          (component.attributes &&
            component.attributes.items &&
            component.attributes.items.value) ||
          [];

        items.forEach((item) => {
          item.duration = this.templateEditorUtils.intValueFor(
            item.duration,
            10,
          );

          var attributes = (item.element && item.element.attributes) || {};
          if (attributes.minfontsize) {
            attributes.minfontsize = this.templateEditorUtils.intValueFor(
              attributes.minfontsize,
              null,
            );
          }
          if (attributes.maxfontsize) {
            attributes.maxfontsize = this.templateEditorUtils.intValueFor(
              attributes.maxfontsize,
              null,
            );
          }
        });
      } else if (component && component.type === 'rise-play-until-done') {
        component.nonEditable = true;
      }
    });
  }

  _getBlueprint(productCode, readOnly) {
    var url = environment.BLUEPRINT_URL.replace('PRODUCT_CODE', productCode);

    //show loading spinner
    this.loadingBlueprint = true;

    return this.httpClient
      .get(url)
      .toPromise()
      .then((response: any) => {
        this._sanitizeBlueprint(response);

        if (!readOnly) {
          this.blueprintData = response;
        }

        this._blueprints[productCode] = response;

        return response;
      })
      .catch((err) => {
        console.error('Failed to get blueprint', err);
        return Promise.reject(err);
      })
      .finally(() => {
        this.loadingBlueprint = false;
      });
  }

  async isPlayUntilDone(productCode?): Promise<boolean> {
    // ai generated presentations do not have a product code and are not PUD
    if (!productCode) {
      return false;
    }

    const result = await this.getBlueprintCached(productCode, true);
    return !!(result && result.playUntilDone);
  }

  hasBranding() {
    return !!this.blueprintData && this.blueprintData.branding === true;
  }

  isRiseInit() {
    return !!this.blueprintData && this.blueprintData.riseInit === true;
  }

  componentFor(componentId: string): any {
    if (!this.blueprintData) {
      return undefined;
    }

    if (componentId && componentId.indexOf(' ') !== -1) {
      return this._playlistItemComponentFor(componentId);
    }

    const components = this.blueprintData.components;
    return _.find(components, {
      id: componentId,
    });
  }

  // Playlist-item ids are synthesized by BasePlaylistService.getComponentObject()
  // as "<playlistId> <index>" (see attribute-data.service.ts#_componentFor for the
  // sibling resolution used by attribute reads/writes). There is no top-level
  // blueprint component for these ids: the item lives inside the parent
  // rise-playlist's sanitized `items` array (see _sanitizeBlueprint above), under
  // `element.tagName` / `element.attributes` — flat, unwrapped values, unlike a
  // top-level component's `{label, value}`-wrapped attributes. Re-wrap them here
  // so callers of getBlueprintData() can't tell the difference.
  private _playlistItemComponentFor(componentId: string): any {
    const tokens = componentId.split(' ');
    const playlistId = tokens[0];
    const items = this.getBlueprintData(playlistId, 'items');
    const item = items && items[tokens[1]];

    if (!item || !item.element) {
      return undefined;
    }

    return {
      id: componentId,
      type: item.element.tagName,
      helpText: item.element.helpText,
      attributes: _.mapValues(item.element.attributes || {}, (value) => ({
        value,
      })),
    };
  }

  getBlueprintData(componentId, attributeKey?) {
    var component = this.componentFor(componentId);

    if (!component || !component.attributes) {
      return null;
    }

    var attributes = component.attributes;

    // if the attributeKey is not provided, it returns the full attributes structure
    if (!attributeKey) {
      return attributes;
    }

    var attribute = attributes[attributeKey];
    return attribute && attribute.value;
  }

  getLogoComponents(blueprintData?) {
    var components = (blueprintData || this.blueprintData).components;

    return _.filter(components, function (c) {
      return (
        c.type === 'rise-image' &&
        c.attributes &&
        c.attributes['is-logo'] &&
        c.attributes['is-logo'].value === 'true'
      );
    });
  }

  getHelpText(componentId) {
    var component = this.componentFor(componentId);

    return component && component.helpText;
  }

  getOrientationPresetAttributeData(orientation: Orientation): {
    orientationPreset: Orientation;
    widthPreset: number;
    heightPreset: number;
  } {
    const isPortrait = orientation === 'portrait';

    return {
      orientationPreset: orientation,
      widthPreset: isPortrait ? 1080 : 1920,
      heightPreset: isPortrait ? 1920 : 1080,
    };
  }

  buildAttributeData(blueprint?: Blueprint): any {
    const bp = blueprint || this.blueprintData;
    if (!bp || !bp.components) {
      return {};
    }

    const attributeData = {
      components: bp.components.map((c) => ({ id: c.id })),
    };
    this.updateOrientation(attributeData, bp);
    return attributeData;
  }

  updateOrientation(attributeData, blueprintData?) {
    const bp = blueprintData || this.blueprintData;
    const landscape = 'landscape',
      portrait = 'portrait';
    let width, height;

    if (!bp || !bp.width || !bp.height) {
      return;
    }

    width = this.templateEditorUtils.intValueFor(bp.width, 0);
    height = this.templateEditorUtils.intValueFor(bp.height, 0);

    // update attribute data based on the blueprint
    attributeData.width = bp.width;
    attributeData.height = bp.height;

    attributeData.orientation = height > width ? portrait : landscape;

    // if orientationPreset exist i.e. user prefers a specific orientation, then apply user preference
    if (attributeData.orientationPreset) {
      attributeData.orientation = attributeData.orientationPreset;

      if (attributeData.orientationPreset === 'custom') {
        attributeData.width = attributeData.widthPreset ?? bp.width;
        attributeData.height = attributeData.heightPreset ?? bp.height;
      }
    }
  }
}
